import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConventionRegistration } from 'app/models/convention-registration.model';
import { SectoralMeeting } from 'app/models/sectoral-meeting.model';
import { LinkHEUConvention } from 'app/models/link-h-e-u-convention.model';
import { Lov } from 'app/models/lov.model';
import { BaseService, PhysicalAddress, LinkLookup } from '@mrm/core';

@Injectable({
  providedIn: 'root'
})
export class ConventionRegistrationService extends BaseService {
  private _conventionRegistrationURL = 'EM/ConventionOnlineRegistration/';

  constructor(private _http: HttpClient) {
    super();
  }

  getRegistration(registrationType: number, id: string) {
    return this._http.get<ConventionRegistration[]>(this._conventionRegistrationURL + 'GetRegistration/' + registrationType + '/' + id);
  }

  save(registrations: ConventionRegistration[]) {
    return this._http.post<ConventionRegistration>(this._conventionRegistrationURL + 'Save', registrations);
  }

  saveSignature(registration: ConventionRegistration) {
    return this._http.post<ConventionRegistration>(this._conventionRegistrationURL + 'SaveSignature', registration);
  }

  previewNotification(recipients: string, languageDBID: number, registration: ConventionRegistration) {
    return this._http.post<ConventionRegistration>(this._conventionRegistrationURL + 'PreviewNotification/' + recipients + '/' + languageDBID, registration);
  }

  sendNotification(recipients: string, languageDBID: number, registration: ConventionRegistration) {
    return this._http.post<ConventionRegistration>(this._conventionRegistrationURL + 'SendNotification/' + recipients + '/' + languageDBID, registration);
  }

  getLocalAddress(localOrgNumber: string) {
    return this._http.get<PhysicalAddress>(this._conventionRegistrationURL + 'GetLocalAddress/' + localOrgNumber);
  }

  getSectoralMeetings(registrationType: number, id: string) {
    return this._http.get<SectoralMeeting[]>(this._conventionRegistrationURL + 'GetSectoralMeetings/' + registrationType + '/' + id);
  }

  getConventionLanguages(registrationType: number, id: string) {
    return this._http.get<LinkLookup[]>(this._conventionRegistrationURL + 'GetConventionLanguages/' + registrationType + '/' + id);
  }

  getConventionMinorityGroups(registrationType: number, id: string) {
    return this._http.get<LinkLookup[]>(this._conventionRegistrationURL + 'GetConventionMinorityGroups/' + registrationType + '/' + id);
  }

  getConventionGenders(registrationType: number, id: string) {
    return this._http.get<LinkLookup[]>(this._conventionRegistrationURL + 'GetConventionGenders/' + registrationType + '/' + id);
  }

  getConventionAttendeeTypes(registrationType: number, id: string) {
    return this._http.get<LinkLookup[]>(this._conventionRegistrationURL + 'GetConventionAttendeeTypes/' + registrationType + '/' + id);
  }

  getHeuValues(registrationType: number, id: string) {
    return this._http.get<LinkHEUConvention[]>(this._conventionRegistrationURL + 'GetHeuValues/' + registrationType + '/' + id);
  }

  getLookups() {
    return this._http.get<Lov>(this._conventionRegistrationURL + 'GetLookups');
  }

  getLookupsForConvention(registrationType: number, id: string) {
    return this._http.get<Lov>(this._conventionRegistrationURL + 'GetLookupsForConvention/' + registrationType + '/' + id);
  }

  isUniqueEmail(conventionDBID: number, emailAddress: string) {
    return this._http.get<boolean>(this._conventionRegistrationURL + 'IsUniqueEmail/' + conventionDBID + '/' + emailAddress);
  }

  // Created this function to return the aggregate type information of all registrations (for alternates) for _setVisibility.
  // This will allow showing the Payment/signature fields.
  getAggregateRegistration(conventionRegistrations: ConventionRegistration[]): ConventionRegistration {
    let registration = new ConventionRegistration();
    if (conventionRegistrations != null && conventionRegistrations.length > 0) {
      // Ensure we have one record with isRegistered (since this determines whether Sec/Pres signatures should be visible)
      registration.isRegistered = conventionRegistrations.filter(x => x.conventionRegistrationDBID > 0 && x.isRegistered).length > 0;
      registration.isCancelled = false; // Can't cancel alternate registrations so just return false
      registration.isConventionAssistFund = conventionRegistrations[0].isConventionAssistFund; // All records will have same value
      registration.processedEntityTypeDBID = conventionRegistrations[0].processedEntityTypeDBID; // All records will have same value
      registration.isPaymentClosed = conventionRegistrations[0].isPaymentClosed; // All records will have same value
      registration.linkHeuConvention = null;
      registration.conventionRegistrationSectoralMeetings = null;
    }

    return registration;
  }

  getFundingSource() {
    return this._http.get<string>(this._conventionRegistrationURL + 'GetFundingSource');
  }
}
